import { useMutation } from '@apollo/client';
import { gql } from '__generated__/gql';
import { UnitStatementPeriodEnumType, UnitStatementYearEnumType } from '__generated__/graphql';
import { FC, useRef, useState } from 'react';

import Button from 'primitives/Button';
import { Dialog, DialogActions, DialogPanel, DialogTitle } from 'primitives/Dialog';

import { FormInput } from 'components/FormPanel';
import { FormPanel } from 'components/FormPanel';

const GENERATE_UNIT_STATEMENT_MUTATION = gql(`
  mutation GenerateUnitStatement($investmentEntityId: ID!, $period: UnitStatementPeriodEnumType!, $year: UnitStatementYearEnumType!) {
    generateUnitStatement(investmentEntityId: $investmentEntityId, period: $period, year: $year) {
      id
      type
      name
      description
      documentDate
      createdAt
      updatedAt
    }
  }
`);

const GenerateUnitStatementDialogButton: FC<{
  investmentEntityId: string;
}> = ({ investmentEntityId }) => {
  const [showDialog, toggleDialog] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const [generateUnitStatement, { loading, error }] = useMutation(
    GENERATE_UNIT_STATEMENT_MUTATION,
    {
      update(cache, { data }) {
        if (!data?.generateUnitStatement) return;

        // Check if document already exists in cache
        const existingDoc = cache.identify({
          id: data.generateUnitStatement.id,
          __typename: 'InvestmentEntityDocumentType',
        });

        if (!existingDoc) {
          // If document doesn't exist, add it to the documents list
          cache.modify({
            id: `InvestmentEntityType:${investmentEntityId}`,
            fields: {
              investmentEntityDocuments(existingDocs = []) {
                const newDocRef = cache.writeFragment({
                  data: data.generateUnitStatement,
                  fragment: gql(`
                    fragment NewDocument on InvestmentEntityDocumentType {
                      id
                      type
                      name
                      status
                      description
                      documentDate
                      createdAt
                      updatedAt
                    }
                  `),
                });
                return [newDocRef, ...existingDocs];
              },
            },
          });
        }
      },
    }
  );

  function renderContent() {
    return (
      <DialogPanel>
        <DialogTitle>Generate Unit Statement</DialogTitle>
        <FormPanel
          loading={loading}
          error={error}
          onCancel={() => toggleDialog(false)}
          onSubmit={data => {
            const [period, year] = data.periodAndYear.split(' ');
            // Todo: Make this more robust. This is a hack to get the period and year.
            const periodEnum =
              period === 'H1' ? UnitStatementPeriodEnumType.H1 : UnitStatementPeriodEnumType.H2;
            const yearEnum =
              year === '2025'
                ? UnitStatementYearEnumType.Year_2025
                : UnitStatementYearEnumType.Year_2026;

            generateUnitStatement({
              variables: {
                investmentEntityId,
                period: periodEnum,
                year: yearEnum,
              },
            }).then(() => toggleDialog(false));
          }}
          buttonRef={buttonRef}
        >
          <FormInput
            type="select"
            fieldName="periodAndYear"
            label="Period and Year"
            defaultValue={''}
            options={[
              {
                label: 'H1 2026',
                value: 'H1 2026',
              },
              {
                label: 'H2 2025',
                value: 'H2 2025',
              },
            ]}
            validators={{ required: true }}
          />
        </FormPanel>
        <DialogActions>
          <Button
            variant="secondary"
            loading={loading}
            onClick={() => {
              // @ts-ignore
              buttonRef.current ? buttonRef.current.cancel() : toggleDialog(false);
            }}
          >
            Cancel
          </Button>
          <Button
            loading={loading}
            onClick={() => {
              // @ts-ignore
              buttonRef.current?.submit();
            }}
          >
            Submit
          </Button>
        </DialogActions>
      </DialogPanel>
    );
  }

  return (
    <>
      <Button onClick={() => toggleDialog(true)}>Generate Unit Statement</Button>
      <Dialog open={showDialog} onClose={() => toggleDialog(false)}>
        {renderContent()}
      </Dialog>
    </>
  );
};

export default GenerateUnitStatementDialogButton;
